import KLineService from '../service/KLine';
import { RealTimeSolutionStatus } from './common/enum';

/**
 * KLineGetDataController类定义
 */
class KLineGetDataController {
    /**
     * KLineConditionController类构造函数
     * @param {String} requestModule 请求的服务器端模块（如：kline、future等）
     * @param {Number} interval 请求的时间间隔
     */
    constructor(requestModule, interval = 60000) {
        this.service = new KLineService(requestModule);
        this.interval = interval > 0 ? interval : 60000;
        this.selectedCondition = {};
        this.timer = -1;
        // 因为有定时器，无法返回每次的Promise，所以改用记录返回结果，在组件上watch返回的response方案
        // 为了让Vue组件监听到，在constructor中先声明一下
        this.response = {};

        this.initResponseObj();
    }

    /**
     * 初始化响应对象
     */
    initResponseObj() {
        this.response = {
            kline: {},
            price: {},
            markPoint: {},
            realTimeSolutionStatus: RealTimeSolutionStatus.nothing
        };
    }

    /**
     * 启动定时器轮询数据
     * @param {Object} selectedCondition 选中条件
     * @param {Array<String>} getDataTypes 获取哪些类型数据
     */
    startInterval(selectedCondition = {}, getDataTypes) {
        this.selectedCondition = selectedCondition;

        // 判断地址栏传递的参数是否正确
        if (!this.JudgmentGetDataParametersIsValid()) {
            return;
        }

        this.clearInterval();
        this.getData(getDataTypes).then(() => {
            this.timer = window.setInterval(() => {
                this.getData(getDataTypes);
            }, this.interval);
        });
    }

    /**
     * 判断地址栏参数是否正确
     * @returns {Boolean} 返回参数是否正确
     */
    JudgmentGetDataParametersIsValid() {
        return ['base', 'quote', 'exchange', 'type'].every(key => {
            if (this.selectedCondition.hasOwnProperty(key)) {
                const value = this.selectedCondition[key];
                if (typeof value !== 'string' || value === '') {
                    return false;
                }
            }
            return true;
        });
    }

    /**
     * 停止定时器
     */
    clearInterval() {
        window.clearInterval(this.timer);
    }

    /**
     * 获取市场行情页面需要的所有数据
     * @param {Array<String>} getDataTypes 获取哪些类型的数据
     * @returns {Promise} 返回Promise对象
     */
    getData(getDataTypes) {
        let promises = [];
        let promiseIndexMap = {};
        getDataTypes.forEach(type => {
            promiseIndexMap[type] = promises.length;
            switch (type) {
                case 'KLINE':
                    promises.push(this.getKLineData());
                    break;
                case 'PRESSURELINE':
                    promises.push(this.getPressureLineData());
                    break;
                case 'NETFLOW':
                    promises.push(this.getNetInflowData());
                    break;
                case 'REALTIMESOLUTION':
                    promises.push(this.getRealTimeSolutionData());
                    break;
                case 'ABNORMALTRANSACTION':
                    promises.push(this.getAbnormalTransactionData());
                    break;
            }
        });

        return Promise.all(promises).then(responses => {
            this.initResponseObj();
            getDataTypes.forEach(type => {
                let datas = responses[promiseIndexMap[type]];
                let key = type.toLowerCase();
                switch (type) {
                    case 'KLINE': // K线标准数据
                        this.response.kline = datas.kline;
                        this.response.price = datas.price;
                        break;
                    case 'PRESSURELINE': // 压力线数据
                        // 因为数据缺失，所以需要按照K线的数据量补充
                        datas.forEach(oneSeries => {
                            oneSeries.datas = this.response.kline.times.map(datetime => {
                                let value = null;
                                oneSeries.datas.some(data => {
                                    if (data.datetime.toString() === datetime) {
                                        value = data.value;
                                        return true;
                                    }
                                    return false;
                                });
                                return value;
                            });
                        });
                        this.response.kline[key] = datas;
                        break;
                    case 'NETFLOW': // 净流量
                        // 因为数据缺失，所以需要按照K线的数据量补充
                        datas = this.response.kline.times.map(datetime => {
                            let netCount = null;
                            datas.some(data => {
                                if (data.datetime.toString() === datetime) {
                                    netCount = data.netCount;
                                    return true;
                                }
                                return false;
                            });
                            return netCount;
                        });
                        this.response.kline[key] = datas;
                        break;
                    case 'REALTIMESOLUTION': // 实时解盘数据
                        this.response.realTimeSolutionStatus = RealTimeSolutionStatus.nothing;
                        if (datas.length > 0) {
                            this.response.realTimeSolutionStatus = this.selectedCondition.realTimeSolutionStatus;
                        }
                        this.response.markPoint[key] = datas;
                        break;
                    case 'ABNORMALTRANSACTION': // 异常交易
                        this.response.markPoint[key] = datas;
                        break;
                }
            });
        });
    }

    /**
     * 获取行情数据
     * @returns {Promise} 返回Promise对象
     */
    getKLineData() {
        return this.service.requestKLineData(this.selectedCondition);
    }

    /**
     * 获取压力线数据
     * @returns {Promise} 返回Promise对象
     */
    getPressureLineData() {
        return this.service.requestPressureLineData(this.selectedCondition);
    }

    /**
     * 获取净流入数据
     * @returns {Promise} 返回Promise对象
     */
    getNetInflowData() {
        return this.service.getNetFlowData(this.selectedCondition);
    }

    /**
     * 获取实时解盘数据
     * @returns {Promise} 返回Promise对象
     */
    getRealTimeSolutionData() {
        return this.service.requestRealTimeSolutionData(this.selectedCondition);
    }

    /**
     * 获取异常交易数据
     * @returns {Promise} 返回Promise对象
     */
    getAbnormalTransactionData() {
        return this.service.requestAbnormalTransactionData(this.selectedCondition);
    }
}

export default KLineGetDataController;



// WEBPACK FOOTER //
// ./src/controller/KLineGetData.js